import { Alert, CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import MuiCard from '@mui/material/Card';
import CssBaseline from '@mui/material/CssBaseline';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiContext } from '../components/ApiProvider';
import StyledButton from '../components/StyledButton';
import StyledTextField from '../components/StyledTextField';
import { TourOperatorBrand } from '../components/TourOperatorBrand';
import { useTourOperatorMeta } from '../hooks/useTourOperatorMeta';


const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
        maxWidth: '450px',
    },
    boxShadow:
        'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
    ...theme.applyStyles('dark', {
        boxShadow:
            'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
    }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
    height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
    minHeight: '100%',
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(4),
    },
    '&::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        zIndex: -1,
        inset: 0,
        backgroundImage:
            'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
        backgroundRepeat: 'no-repeat',
        ...theme.applyStyles('dark', {
            backgroundImage:
                'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
        }),
    },
}));

export default function SignIn(props) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [data, setData] = React.useState({
        "bref": searchParams.get('b') || '',
        "last_name": searchParams.get('l') || '',
    });
    const [autologin, setAutologin] = React.useState(!!searchParams.get('al'));
    const autologinTriggered = React.useRef(false);
    const { login, loginError, isActive } = React.useContext(ApiContext);
    const { operatorMeta, isLoading: isLoadingMeta } = useTourOperatorMeta();
    const [brefError, setBrefError] = React.useState(false);
    const [brefErrorMessage, setBrefErrorMessage] = React.useState('');
    const [lastnameError, setLastnameError] = React.useState(false);
    const [lastnameErrorMessage, setLastnameErrorMessage] = React.useState('');
    const [open, setOpen] = React.useState(false);

    const validateInputs = React.useCallback(() => {
        let isValid = true;

        if (!data.bref || data.bref.length < 3) {
            setBrefError(true);
            setBrefErrorMessage('Please enter a valid booking reference address.');
            isValid = false;
        } else {
            setBrefError(false);
            setBrefErrorMessage('');
        }

        if (!data.last_name || data.last_name.length < 1) {
            setLastnameError(true);
            setLastnameErrorMessage('Last name must be not empty');
            isValid = false;
        } else {
            setLastnameError(false);
            setLastnameErrorMessage('');
        }

        return isValid;
    }, [data.bref, data.last_name]);

    React.useEffect(() => {
        if (!autologin || autologinTriggered.current) { return; }
        autologinTriggered.current = true;
        // A link missing a detail falls back to the form, with the missing detail flagged
        if (!validateInputs()) {
            setAutologin(false);
            return;
        }

        login(data.bref, data.last_name).then((ok) => {
            if (ok) navigate('/participant/0');
        }).finally(() => {
            setAutologin(false);
        });
    }, [data, autologin, login, validateInputs]);

    const onFieldValueChange = (ev) => {
        setData((prev) => ({ ...prev, [ev.target.name]: ev.target.value }));
    }

    const onSubmit = (event) => {
        if (!validateInputs()) {
            event.preventDefault();
            return;
        }
        login(data.bref, data.last_name).then((ok) => {
            if (ok) navigate('/participant/0');
        });
    };

    const onKeyUp = (ev) => {
        if (ev.keyCode === 13) {
            if (!validateInputs()) {
                ev.preventDefault();
                return;
            }
            login(data.bref, data.last_name).then((ok) => {
                if (ok) navigate('/participant/0');
            });
        }
    }

    return (<>
        <CssBaseline enableColorScheme />
        <SignInContainer direction="column" justifyContent="center">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ mb: 8, minHeight: '60px' }}>
                    {isLoadingMeta ? (
                        <CircularProgress size={60} />
                    ) : (
                        <TourOperatorBrand 
                            operator={operatorMeta}
                            showOnMobile={true}
                            larger={true}
                        />
                    )}
                </Box>
                <Card variant="outlined" sx={{ px: 4 }}>
                    <Typography component="h1" variant="h5" sx={{ width: '100%', fontWeight: 800, mt: 0, mb: 3, textAlign: 'center' }}>
                        Manage your booking
                    </Typography>
                    {loginError && (<Alert severity="error">{loginError}</Alert>)}
                    <Box noValidate sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2, alignItems: "center" }}>
                        <StyledTextField
                            label="Enter your booking number"
                            error={brefError}
                            helperText={brefErrorMessage}
                            id="bref"
                            type="text"
                            name="bref"
                            placeholder="123456789"
                            value={data.bref}
                            autoComplete="bref"
                            autoFocus
                            required
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            onChange={onFieldValueChange}
                            onKeyUp={onKeyUp}
                        />
                        <StyledTextField
                            label="Last Name connected with booking"
                            error={lastnameError}
                            helperText={lastnameErrorMessage}
                            name="last_name"
                            placeholder="Lastay"
                            type="text"
                            id="last_name"
                            value={data.last_name}
                            autoComplete="current-last-name"
                            required
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            onChange={onFieldValueChange}
                            onKeyUp={onKeyUp}
                        />
                        {isActive || autologin ?
                            <CircularProgress color='primary' />
                            :
                            <StyledButton fullWidth variant="contained" onClick={onSubmit}>Sign in</StyledButton>
                        }
                    </Box>
                </Card>
            </Box>
        </SignInContainer>
    </>);
}
